/**
 * Theme Service
 *
 * Handles all API calls related to theme management,
 * user preferences, and admin enforcement.
 */

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000';

/**
 * Get authentication token from storage
 */
function getAuthToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token');
}

/**
 * Make authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers
  };

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }

  return response.json();
}

export const themeService = {
  /**
   * Get current user's theme preference
   * @returns {Promise<Object>} User theme data
   */
  async getUserTheme() {
    try {
      return await apiRequest('/api/themes/user');
    } catch (error) {
      // Return null on error to indicate backend unavailable
      return null;
    }
  },

  /**
   * Save user's theme preference
   * @param {string} themeType - 'dark', 'light', or 'custom'
   * @returns {Promise<Object>} Response data
   */
  async saveUserPreference(themeType) {
    return await apiRequest('/api/themes/user', {
      method: 'POST',
      body: JSON.stringify({ themeType })
    });
  },

  /**
   * Save custom theme configuration
   * @param {Object} themeConfig - Complete theme configuration
   * @returns {Promise<Object>} Saved theme with ID
   */
  async saveCustomTheme(themeConfig) {
    return await apiRequest('/api/themes/custom', {
      method: 'POST',
      body: JSON.stringify(themeConfig)
    });
  },

  /**
   * Update existing custom theme
   * @param {number} themeId - Theme ID
   * @param {Object} updates - Theme updates
   * @returns {Promise<Object>} Updated theme
   */
  async updateCustomTheme(themeId, updates) {
    return await apiRequest(`/api/themes/custom/${themeId}`, {
      method: 'PUT',
      body: JSON.stringify(updates)
    });
  },

  /**
   * Delete custom theme
   * @param {number} themeId - Theme ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  async deleteCustomTheme(themeId) {
    return await apiRequest(`/api/themes/custom/${themeId}`, {
      method: 'DELETE'
    });
  },

  /**
   * Get all available themes (admin)
   * @returns {Promise<Array>} List of all themes
   */
  async getAllThemes() {
    try {
      return await apiRequest('/api/themes');
    } catch (error) {
      return [];
    }
  },

  /**
   * Get theme enforcement settings (admin)
   * @returns {Promise<Object>} Enforcement configuration
   */
  async getEnforcementSettings() {
    try {
      return await apiRequest('/api/admin/themes/settings');
    } catch (error) {
      return {
        enabled: false,
        themeType: null,
        customThemeId: null
      };
    }
  },

  /**
   * Enable theme enforcement (admin)
   * @param {string} themeType - Theme type to enforce
   * @param {number} customThemeId - Custom theme ID (if type is 'custom')
   * @returns {Promise<Object>} Enforcement confirmation
   */
  async enforceTheme(themeType, customThemeId = null) {
    return await apiRequest('/api/admin/themes/enforce', {
      method: 'POST',
      body: JSON.stringify({
        themeType,
        customThemeId,
        enabled: true
      })
    });
  },

  /**
   * Disable theme enforcement (admin)
   * @returns {Promise<Object>} Confirmation
   */
  async disableEnforcement() {
    return await apiRequest('/api/admin/themes/enforce', {
      method: 'DELETE'
    });
  },

  /**
   * Get theme usage analytics (admin)
   * @returns {Promise<Object>} Theme usage statistics
   */
  async getThemeAnalytics() {
    try {
      return await apiRequest('/api/admin/themes/analytics');
    } catch (error) {
      return {
        dark: 0,
        light: 0,
        custom: 0,
        total: 0
      };
    }
  }
};

export default themeService;
